import { useEffect, useState } from 'react';
import type { ApiClient, LueftungBerichtsvorbehalte } from '../../api';
import { ErrorState, Vorbehalte } from '../../muster';
import { seitenVorbehalte } from './pruefung';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE VORBEHALTE DER LÜFTUNGSBERICHTE AUF DEN SEITEN (TV7a-2, Plan §22 c)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Schall, Raumschall und Druckverlust zeigen über ihren Zahlen den
 * Vorbehaltskasten IHRES Berichts (LUE-SCH, LUE-RSC, LUE-DRV) - Satz für Satz
 * aus der Antwort `…/vorbehalte`, im selben Umfang wie das PDF. Bis TV7a-2
 * baute jede Seite ihre Sätze selbst, aus anderen Zahlen als der Bericht
 * (Befund TV §19.6).
 *
 * Ein Vorbehalt, der nicht geladen werden kann, ist kein „kein Vorbehalt“:
 * Dann steht eine Meldung da und kein leerer Platz.
 */

export type Berichtsart = 'schall' | 'raumschall' | 'druckverlust';

export interface Berichtsvorbehaltstand {
  daten: LueftungBerichtsvorbehalte | null;
  fehler: unknown;
}

/** Lädt die Vorbehalte eines Berichts; `neu` zählt hoch, wenn neu gerechnet wurde. */
export function useBerichtsvorbehalte(
  api: ApiClient, projektId: string, bericht: Berichtsart, umfang: { anlage?: string; ebene?: string }, neu = 0,
): Berichtsvorbehaltstand {
  const [stand, setStand] = useState<Berichtsvorbehaltstand>({ daten: null, fehler: null });
  const { anlage, ebene } = umfang;

  useEffect(() => {
    let gilt = true;
    setStand({ daten: null, fehler: null });
    api.lueftungBerichtsvorbehalte(projektId, bericht, { anlage, ebene })
      .then((daten) => { if (gilt) setStand({ daten, fehler: null }); })
      .catch((fehler: unknown) => { if (gilt) setStand({ daten: null, fehler }); });
    return () => { gilt = false; };
  }, [api, projektId, bericht, anlage, ebene, neu]);

  return stand;
}

const NAME: Record<Berichtsart, string> = {
  schall: 'des Schallnachweises (LUE-SCH)',
  raumschall: 'des Raumschallnachweises (LUE-RSC)',
  druckverlust: 'der Druckverlustberechnung (LUE-DRV)',
};

/** Der Kasten eines Berichts - wortgleich mit dem PDF -, oder die Meldung, dass er fehlt. */
export function Berichtsvorbehaltkasten({ stand, bericht }: { stand: Berichtsvorbehaltstand; bericht: Berichtsart }) {
  if (stand.fehler !== null) {
    return (
      <ErrorState
        klein
        meldung={`Die Vorbehalte ${NAME[bericht]} konnten nicht abgerufen werden - die Zahlen gelten nur mit ihnen.`}
        technisch={stand.fehler}
      />
    );
  }
  if (!stand.daten) return null;
  return <Vorbehalte eintraege={seitenVorbehalte([stand.daten])} />;
}
